import React, { createContext, useState } from 'react';
import produce from 'immer';
import { SelectedOperation } from './types/SelectedOperation';
import { Status, Threshold } from './types/QaErrors';

type QaErrorsProviderProps = {
  children: React.ReactElement | React.ReactElement[];
};

export type Filters = {
  createdStart: string | null;
  createdEnd: string | null;
  applicationId: string | null;
  priority: string | null;
  scriptName: string | null;
  siteCode: string | null;
  errorCode: string | null;
  threshold: Threshold | null;
  status: Status | null;
  scriptHealthMin: number | null;
  scriptHealthMax: number | null;
};

type QaErrorsProviderState = {
  filters: Filters;
  selectedOperation: SelectedOperation | null;
};

type QaErrorsContextValue = QaErrorsProviderState & {
  changeFilters: <T extends keyof Filters>(filterKey: T, value: Filters[T]) => void;
  changeScriptHealth: (value: [number, number]) => void;
  setSelectedOperation: (value: SelectedOperation | null) => void;
};

const initialState: QaErrorsProviderState = {
  filters: {
    createdStart: null,
    createdEnd: null,
    applicationId: null,
    priority: null,
    scriptName: null,
    siteCode: null,
    errorCode: null,
    threshold: null,
    status: null,
    scriptHealthMin: null,
    scriptHealthMax: null,
  },
  selectedOperation: null,
};

export const QaErrorsContext = createContext<QaErrorsContextValue>({
  ...initialState,
  changeFilters: () => {},
  changeScriptHealth: () => {},
  setSelectedOperation: () => {},
});

export const QaErrorsProvider: React.FC<QaErrorsProviderProps> = ({ children }) => {
  const [state, setState] = useState(initialState);

  const value: QaErrorsContextValue = {
    filters: state.filters,
    selectedOperation: state.selectedOperation,
    setSelectedOperation: (value: SelectedOperation | null) => {
      setState((prevState) => ({ ...prevState, selectedOperation: value }));
    },
    changeFilters: <T extends keyof Filters>(filterKey: T, value: Filters[T]) => {
      setState(
        produce((draft: QaErrorsProviderState) => {
          draft.filters[filterKey] = value;
        })
      );
    },
    changeScriptHealth: (value) => {
      setState(
        produce((draft: QaErrorsProviderState) => {
          draft.filters.scriptHealthMin = value[0];
          draft.filters.scriptHealthMax = value[1];
        })
      );
    },
  };

  return <QaErrorsContext.Provider value={value}>{children}</QaErrorsContext.Provider>;
};
